import { memo, useState } from 'react'

import { Edit, Warning } from '@mui/icons-material'
import { Box, Button, Chip, Paper, styled, Typography } from '@mui/material'
// NOTE: exp-analytics not deployed — restore these into their imports with the analytics panel below.
// import { ButtonBase } from '@mui/material'
// import { motion } from 'motion/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { DetailRow } from '@shared/components/DetailRow'
import { ErrorBoundary } from '@shared/components/ErrorHandling/ErrorBoundary'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import GrafanaLogo from 'assets/logos/grafana.svg?react'
import { ConfigForm } from 'domains/Workspaces/Events/Event/ConfigEditor/ConfigForm'
import { readFixtureId } from 'domains/Workspaces/Events/Event/ConfigEditor/model'
import { EventAlerts, EventState, Verification } from 'domains/Workspaces/Events/Event/Dashboard'
import { EventProgress } from 'domains/Workspaces/Events/Event/Dashboard/EventProgress'
import { Panel as OnCallPanel } from 'domains/Workspaces/Events/Event/Dashboard/OnCall/Panel'
import { StatusOverview } from 'domains/Workspaces/Events/Event/StatusOverview'
import { StateVisualizer } from 'domains/Workspaces/Events/layouts'
import { eventDashboardRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'
// NOTE: exp-analytics is currently not deployed; the analytics panel is replaced by a placeholder below.
// import { reportQueryKeys } from 'services/queryKeys/exp-analytics'
import { RefetchIntervals } from 'types/query'

const StyledGrafanaLogo = styled(GrafanaLogo)({
  width: 60,
})

const DashboardRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const { eventId, workspaceId } = eventDashboardRoute.useParams()

  const [editing, setEditing] = useState(false)

  const eventQuery = useQuery({ ...eventQueryKeys.detail({ eventId }), refetchInterval: RefetchIntervals.NORMAL })
  const { data: event } = eventQuery

  // NOTE: exp-analytics is currently not deployed; the analytics dashboards query is disabled.
  // const expManifestAnalyticsQuery = useQuery({
  //   ...reportQueryKeys.dashboards({ groupId: `manifest-${event?.expManifestId}` }),
  //   enabled: eventQuery.isSuccess,
  // })

  // Keep showing the indicator while a transient failure is being retried (e.g. a 401 that the
  // global handler refetches after a token refresh) — otherwise the 404 page flashes in between.
  if (eventQuery.isPending || (!event && eventQuery.isFetching)) {
    return <LoadingIndicator />
  }

  if (!event) {
    return <NotFound />
  }

  const fixtureId = event.config ? readFixtureId(event.config) : ''
  const tags = Array.from(event.config?.tags ?? [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 4, flexGrow: 1, minHeight: 0 }}>
      {editing && event.config ? (
        <Box sx={{ flexGrow: 1, minHeight: 0, bgcolor: 'background.levels.100', borderRadius: 1, overflow: 'hidden' }}>
          <ConfigForm
            event={event}
            config={event.config}
            workspaceId={workspaceId}
            showSchedule
            showOnCall
            onSaved={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 4, flexShrink: 0, pb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
            <Typography variant="heading-lg" noWrap sx={{ minWidth: 0 }}>
              {event.status?.name || t('noNameProvided')}
            </Typography>
            {event.state != null && event.health != null && (
              <StateVisualizer variant="text-sm" state={event.state} health={event.health}>
                {event.state}
              </StateVisualizer>
            )}
            {event.config && (
              <Button sx={{ ml: 'auto' }} startIcon={<Edit />} variant="outlined" onClick={() => setEditing(true)}>
                {t('domain:Workspace.Event.edit')}
              </Button>
            )}
          </Box>

          <ErrorBoundary
            fallback={() => (
              <Paper
                sx={{
                  height: 130,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  columnGap: 2,
                }}
                elevation={0}
              >
                <Warning color="warning" />
                <Typography sx={{ color: 'warning.main' }} variant="heading-lg">
                  {t('domain:Workspace.Event.componentUnavailable')}
                </Typography>
                <Warning color="warning" />
              </Paper>
            )}
          >
            <EventProgress event={event} />
          </ErrorBoundary>

          <Paper
            sx={{
              px: 4,
              py: 3,
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 6,
              rowGap: 2,
              '& > *': { minWidth: 180, maxWidth: 320 },
            }}
            elevation={0}
          >
            <DetailRow label={t('domain:Workspace.Event.manifestId')} value={event.expManifestId} />
            <DetailRow label={t('domain:Workspace.Event.fixtureId')} value={fixtureId} />
            <DetailRow label={t('domain:Workspace.Event.tags')}>
              {tags.length ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {tags.map((tag) => (
                    <Chip key={tag} size="small" label={tag} />
                  ))}
                </Box>
              ) : null}
            </DetailRow>
          </Paper>

          <Box sx={{ bgcolor: 'background.levels.100', borderRadius: 1, overflow: 'hidden' }}>
            <StatusOverview eventId={event.id} hideHeader fitContent />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 2 }}>
            <EventState
              eventStatus={event.status}
              health={event.health}
              state={event.state}
              fetchedAt={eventQuery.dataUpdatedAt}
            />

            <Paper sx={{ px: 4, py: 3, flex: 1, minWidth: 320 }}>
              <Typography sx={{ fonWeight: 500, mb: 1 }} variant="heading-md">
                {t('domain:Workspace.Event.details')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 1 }}>
                <DetailRow label={t('domain:Workspace.Event.eventId')} value={event.id} />
                <DetailRow label={t('domain:Workspace.Event.taskId')} value={event.taskId} />
              </Box>
            </Paper>

            <Verification event={event} />
          </Box>

          <OnCallPanel event={event} />

          <EventAlerts eventAlerts={event.alerts} />

          {/* exp-analytics not deployed — original Grafana analytics panel preserved below for easy restore.
          Re-enable: uncomment the reportQueryKeys import, the expManifestAnalyticsQuery, the
          motion/ButtonBase imports above, and this block; then delete the placeholder Paper underneath.

      {expManifestAnalyticsQuery.isSuccess && (
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
          {!!expManifestAnalyticsQuery.data.manifestTimeline?.length && (
            <ButtonBase
              sx={{ font: 'unset' }}
              component={motion.a}
              whileHover={{
                scale: 1.05,
              }}
              href={expManifestAnalyticsQuery.data.manifestTimeline}
            >
              <Paper
                sx={{
                  p: 3,
                  width: 160,
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  textDecoration: 'none',
                }}
              >
                <StyledGrafanaLogo sx={{ mb: 2 }} />
                <Typography variant="heading-sm">Manifest</Typography>
              </Paper>
            </ButtonBase>
          )}

          {!!expManifestAnalyticsQuery.data.wallclockTimeline?.length && (
            <ButtonBase
              sx={{ font: 'unset' }}
              component={motion.a}
              whileHover={{
                scale: 1.05,
              }}
              href={expManifestAnalyticsQuery.data.wallclockTimeline}
            >
              <Paper
                sx={{
                  p: 3,
                  width: 160,
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  textDecoration: 'none',
                }}
              >
                <StyledGrafanaLogo sx={{ mb: 2 }} />
                <Typography variant="heading-sm">Wallclock</Typography>
              </Paper>
            </ButtonBase>
          )}
        </Box>
      )}
      */}
          <Paper
            sx={{
              px: 4,
              py: 3,
              minWidth: 320,
              maxWidth: 480,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              columnGap: 2,
            }}
            elevation={0}
          >
            <StyledGrafanaLogo />
            <Box>
              <Typography variant="heading-sm">{t('domain:Workspace.Event.analytics')}</Typography>
              <Typography sx={{ color: 'text.secondary' }} variant="text-sm">
                {t('domain:Workspace.Event.analyticsUnavailable')}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  )
}

export const Dashboard = memo(DashboardRaw)
